import {
  FC,
  useEffect,
  useState,
} from "react"
import './createProjectModal.scoped.scss'
import { ModalBase } from "components/common/modals/modalBase/modalBase"
import { ModalProps } from "services/modal/modalService"
import { PrimaryButton } from "components/common/buttons/primaryButton/primaryButton"
import { LightButton } from "components/common/buttons/lightButton/lightButton"
import * as yup from "yup"
import { FormikHelpers, useFormik } from "formik"
import { TextField } from "components/common/formFields/textField/textField"
import { useSicalcApiService } from "services/sicalcApi/sicalcApiService"
import { useAuthService } from "services/auth/authService"
import { toast } from "react-hot-toast"
import { TextAreaField } from "components/common/formFields/textAreaField/textAreaField"
import { NoiseSourceInput } from "components/common/formFields/noiseSourceInput/noiseSourceInput"
import {
  CompanyField,
  CompanyFieldValues,
  companyFieldValidation,
  initialCompanyFieldValues,
  useCompanies,
} from "components/common/formFields/companyField/companyField"
import { getFieldErrorHelpers, ServerFieldError } from "utils/formFieldErrors"

export interface CreateProjectModalProps extends ModalProps {

}

interface FormDataModel extends CompanyFieldValues {
  projectName: string
  defaultNoiseSourceId: string
  projectNotes: string
  projectReference: string
  customersClientName: string
  invoiceEmail: string
}

// Matches the limits on the server's CreateProjectRequest
const tooLongMessage = (maxLength: number) => `For langt (maks ${maxLength} tegn)`

const formValidationSchema = yup.object().shape({
  ...companyFieldValidation,
  projectName: yup.string()
    .required("Påkrevd")
    .min(1, "Må bestå av minst ett tegn")
    .max(70, "Navnet er for langt (maks 70 tegn)"),
  projectNotes: yup.string()
    .max(1800, "Maks 1800 tegn"),
  projectReference: yup.string()
    .trim()
    .max(250, tooLongMessage(250)),
  customersClientName: yup.string()
    .trim()
    .required("Påkrevd")
    .max(250, tooLongMessage(250)),
  invoiceEmail: yup.string()
    .trim()
    .required("Påkrevd")
    .email("Må være en e-postadresse")
    .max(250, tooLongMessage(250)),
})

export const CreateProjectModal: FC<CreateProjectModalProps> = (props) => {
  const { projectApi, authenticationApi } = useSicalcApiService()
  const { currentUserId } = useAuthService()
  const { companies, createCompany } = useCompanies()

  const [ serverFieldErrors, setServerFieldErrors ] = useState<ServerFieldError<FormDataModel>[]>([])

  const handleSubmission = async (values: FormDataModel, helpers: FormikHelpers<FormDataModel>) => {
    setServerFieldErrors([])
    let companyId = values.companyId

    if (values.isNewCompany) {
      const result = await createCompany(values)
      if ("conflicts" in result) {
        setServerFieldErrors(result.conflicts)
        return
      }
      if ("errorMessage" in result) {
        toast.error(result.errorMessage)
        return
      }
      companyId = result.companyId

      // The company exists from now on, so trying again must not create it a second time
      await helpers.setValues({
        ...values,
        isNewCompany: false,
        companyId,
      }, false)
    }

    const { projectName, defaultNoiseSourceId, projectNotes, projectReference } = values
    try {
      const result = await projectApi.create({
        name: projectName.trim(),
        notes: projectNotes.trim(),
        description: projectReference.trim(),
        customersClientName: values.customersClientName.trim(),
        invoiceEmail: values.invoiceEmail.trim(),
        companyId,
        defaultNoiseSourceId,
      })
      props.modalContext.close(result)
    } catch {
      toast.error(values.isNewCompany
        ? "Firmaet ble opprettet, men prosjektet kunne ikke opprettes"
        : "Kunne ikke opprette prosjektet")
    }
  }

  const initialValues: FormDataModel = {
    ...initialCompanyFieldValues,
    projectName: '',
    defaultNoiseSourceId: '',
    projectNotes: '',
    projectReference: '',
    customersClientName: '',
    invoiceEmail: '',
  }

  const form = useFormik({
    initialValues,
    validationSchema: formValidationSchema,
    onSubmit: handleSubmission,
  })

  const { isInvalid, errorsFor } = getFieldErrorHelpers(form, serverFieldErrors)

  const { setValues } = form

  useEffect(() => {
    if (currentUserId === undefined) {
      return
    }

    // Ignore a response that arrives after the modal closed or the user changed
    let ignore = false

    authenticationApi.getCompanyIdForUser(currentUserId)
      .then(companyId => {
        // The invoice goes to the user's own company by default, unless they picked another while loading
        if (!ignore && companyId !== null) {
          setValues(values => values.companyId === ''
            ? {
              ...values,
              companyId,
            }
            : values, false)
        }
      })
      .catch(() => {
        // Without a default, the user just picks the company themselves
      })

    return () => {
      ignore = true
    }
  }, [authenticationApi, currentUserId, setValues])

  const dismiss = () => {
    props.modalContext.dismiss()
  }

  return (
    <ModalBase
      onDismiss={dismiss}
      heading="Nytt prosjekt"
      actions={
        <>
          <LightButton onClick={dismiss}>Avbryt</LightButton>
          <PrimaryButton onClick={form.handleSubmit} disabled={form.isSubmitting}>
            Opprett
          </PrimaryButton>
        </>
      }
    >
      <form className="create-project-form column gap4">
        <TextField
          label="Prosjektnavn*"
          name="projectName"
          value={form.values.projectName}
          isInvalid={isInvalid("projectName")}
          errors={errorsFor("projectName")}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
        />
        <TextAreaField
          label="Beskrivelse"
          hint="Denne teksten vil bli inkludert i rapporten for å beskrive prosjektet"
          name="projectNotes"
          value={form.values.projectNotes}
          isInvalid={isInvalid("projectNotes")}
          errors={errorsFor("projectNotes")}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          rows={6}
        />
        <TextField
          label="Prosjektreferanse"
          hint="Prosjektnummeret, dette vil vises på fakturaen"
          name="projectReference"
          value={form.values.projectReference}
          isInvalid={isInvalid("projectReference")}
          errors={errorsFor("projectReference")}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
        />

        <CompanyField
          label="Fakturamottaker*"
          newCompanyHeading="Fakturamottaker – nytt firma"
          form={form}
          companies={companies}
          isInvalid={isInvalid}
          errorsFor={errorsFor}
        />
        <TextField
          label="Fakturareferanse*"
          hint="Navnet på kontaktpersonen som bestiller oppdraget"
          name="customersClientName"
          autoComplete="off"
          value={form.values.customersClientName}
          isInvalid={isInvalid("customersClientName")}
          errors={errorsFor("customersClientName")}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
        />
        <TextField
          label="Faktura e-post*"
          hint="E-postadressen til fakturareferansen"
          name="invoiceEmail"
          type="email"
          autoComplete="off"
          value={form.values.invoiceEmail}
          isInvalid={isInvalid("invoiceEmail")}
          errors={errorsFor("invoiceEmail")}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
        />

        <label className="column gap1">
          <span className="form-label">Standard støykilde</span>
          <NoiseSourceInput
            value={form.values.defaultNoiseSourceId}
            onChange={value => form.setFieldValue("defaultNoiseSourceId", value)}
            onBlur={form.handleBlur}
          />
        </label>
      </form>
    </ModalBase>
  )
}

